// src/pages/LandingPage.tsx
import React, { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/app/providers/AuthContext';
import SupabaseLoginButton from '@/features/auth/components/SupabaseLoginButton';
import { PricingTier } from '@/features/landing/components/PricingTier';
import ProviderLogos from '@/features/landing/components/ProviderLogos';
import { Link } from 'react-router-dom';
import { Button } from '@/ui/Button';
import { FeaturesSection } from '@/features/landing/components/FeaturesSection';
import { FAQSection } from '@/features/landing/components/FAQSection';
import { SheetLayout } from '@/ui/layout/SheetLayout';
import { Section } from '@/ui/layout/Section';
import { SheetBadge } from '@/ui/layout/SheetBadge';
import { LandingHeader } from '@/features/landing/components/LandingHeader';
import { LandingFooter } from '@/features/landing/components/LandingFooter';

// Main component
const LandingPage: React.FC = () => {
  const { isAuthenticated, isLoading } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    if (isAuthenticated && !isLoading) {
      navigate('/dashboard');
    }
  }, [isAuthenticated, isLoading, navigate]);

  const handleGetStarted = () => {
    // This will trigger the modal in the SupabaseLoginButton
  };

  return (
    <SheetLayout>
      <LandingHeader />

      <main>
        {/* Hero Section */}
        <Section className="py-24 px-8">
          <div className="max-w-4xl mx-auto pt-8">
            <SheetBadge>ABSTRACT</SheetBadge>

            <h1 className="text-5xl md:text-7xl font-bold tracking-tight text-foreground leading-[1.1] mb-8 font-mono">
              AI CODE REVIEWER <br />
              <span className="text-muted-foreground font-normal font-sans">For teams that want consistent code reviews without slowing down.</span>
            </h1>

            <p className="text-xl md:text-2xl text-muted-foreground max-w-2xl leading-relaxed mb-10 border-l-4 border-neutral-950 dark:border-neutral-100 pl-6 py-2">
            Catch bugs, enforce standards, and reduce PR review fatigue with an AI agent that reviews every pull request, 24/7. <span className="text-foreground font-medium">No configuration required.</span>
            </p>

            <div className="flex flex-col sm:flex-row gap-4 pt-4">
              <div className="border border-neutral-950 dark:border-neutral-100 p-1 bg-background">
                <SupabaseLoginButton
                  buttonText="REVIEW YOUR FIRST PR"
                  variant="default"
                  size="lg"
                  showModal={true}
                  className="h-12 px-8 text-base font-bold rounded-none bg-foreground text-background hover:bg-neutral-800 transition-all w-full sm:w-auto"
                />
              </div>
              <div className="border border-neutral-950 dark:border-neutral-100 p-1 bg-background">
                <Link to="/docs">
                  <Button variant="ghost" size="lg" className="h-12 px-8 text-base font-medium rounded-none hover:bg-neutral-100 dark:hover:bg-neutral-900 w-full sm:w-auto">
                    READ DOCUMENTATION
                  </Button>
                </Link>
              </div>
            </div>

            <div className="flex items-center gap-8 text-sm text-muted-foreground pt-12 font-mono uppercase tracking-wider">
              <span className="flex items-center gap-2">
                <div className="w-1.5 h-1.5 bg-neutral-950 dark:bg-neutral-100"></div>
                Zero Setup
              </span>
              {/* <span className="flex items-center gap-2">
                <div className="w-1.5 h-1.5 bg-neutral-950 dark:bg-neutral-100"></div>
                SOC2 Compliant
              </span> */}
              <span className="flex items-center gap-2">
                <div className="w-1.5 h-1.5 bg-neutral-950 dark:bg-neutral-100"></div>
                Multi-LLM Support
              </span>
            </div>
          </div>
        </Section>

        {/* Demo Section */}
        <Section className="py-0 grid lg:grid-cols-2">
          <div className="p-12 space-y-8 border-r-2 border-neutral-950 dark:border-neutral-100 relative">
             <SheetBadge className="mb-2">I. METHODOLOGY</SheetBadge>

            <h2 className="text-3xl font-bold tracking-tight font-mono pt-4">TECHNICAL CAPABILITIES</h2>
            <p className="text-lg text-muted-foreground leading-relaxed">
              Codii connects directly to your GitHub repository. It analyzes pull request diffs using context-aware LLM agents to provide actionable feedback.
            </p>

            <div className="space-y-0 border border-neutral-950 dark:border-neutral-100">
              <div className="p-4 border-b border-neutral-950 dark:border-neutral-100 bg-neutral-50 dark:bg-neutral-900/20">
                <span className="font-mono font-bold mr-3">01.</span>
                <span>Detects logical bugs and security vulnerabilities.</span>
              </div>
              <div className="p-4 border-b border-neutral-950 dark:border-neutral-100">
                <span className="font-mono font-bold mr-3">02.</span>
                <span>Enforces architectural consistency.</span>
              </div>
              <div className="p-4 bg-neutral-50 dark:bg-neutral-900/20">
                <span className="font-mono font-bold mr-3">03.</span>
                <span>Suggests performance optimizations.</span>
              </div>
            </div>
          </div>

          <div className="p-12 bg-neutral-50 dark:bg-neutral-950 flex items-center justify-center relative">
             <div className="absolute top-0 right-0 p-0">
                <div className="bg-foreground text-background text-[10px] font-bold px-2 py-1 uppercase tracking-wider">
                  SUPPORTED APIS
                </div>
             </div>
             <div className="border border-neutral-950 dark:border-neutral-100 w-full shadow-none rounded-none overflow-hidden bg-background">
                <div className="border-b border-neutral-950 dark:border-neutral-100 p-2 px-4 bg-neutral-100 dark:bg-neutral-900 font-mono text-xs flex justify-between">
                  <span>review_example.ts</span>
                  <span>TypeScript</span>
                </div>
               <div className="p-4 font-mono text-xs md:text-sm bg-background">
                  {/* Line 1 */}
                  <div className="flex gap-4 opacity-50">
                      <span className="w-6 text-right select-none text-muted-foreground">12</span>
                      <span><span className="text-blue-600 dark:text-blue-400">async function</span> <span className="text-yellow-600 dark:text-yellow-400">chargeUser</span>(amount: <span className="text-emerald-600 dark:text-emerald-400">number</span>) {'{'}</span>
                  </div>

                  {/* Comment 1: Validation */}
                  <div className="ml-10 my-3 border-l-2 border-neutral-950 dark:border-neutral-100 pl-4 py-2 bg-neutral-50 dark:bg-neutral-900/50">
                      <div className="flex items-center gap-2 mb-1">
                           <div className="w-2 h-2 bg-red-500 rounded-none"></div>
                           <span className="font-bold text-red-600 dark:text-red-400 text-[10px] uppercase tracking-wider">Critical</span>
                           <span className="text-muted-foreground text-[10px]">Validation</span>
                      </div>
                      <p className="text-muted-foreground">Amount is not validated. Consider rejecting zero or negative values before charging.</p>
                  </div>

                  {/* Line 2 */}
                  <div className="flex gap-4">
                      <span className="w-6 text-right select-none opacity-50 text-muted-foreground">13</span>
                      <span>  <span className="text-blue-600 dark:text-blue-400">const</span> result = <span className="text-purple-600 dark:text-purple-400">await</span> payments.charge(amount);</span>
                  </div>

                  {/* Comment 2: Error Handling */}
                  <div className="ml-10 my-3 border-l-2 border-neutral-950 dark:border-neutral-100 pl-4 py-2 bg-neutral-50 dark:bg-neutral-900/50">
                      <div className="flex items-center gap-2 mb-1">
                           <div className="w-2 h-2 bg-yellow-500 rounded-none"></div>
                           <span className="font-bold text-yellow-600 dark:text-yellow-400 text-[10px] uppercase tracking-wider">Warning</span>
                           <span className="text-muted-foreground text-[10px]">Error Handling</span>
                      </div>
                      <p className="text-muted-foreground">payments.charge() may throw on network or provider errors. Wrap the call in a try/catch to return a consistent result.</p>
                  </div>

                  {/* Line 3 */}
                  <div className="flex gap-4 opacity-50">
                      <span className="w-6 text-right select-none text-muted-foreground">14</span>
                      <span>  <span className="text-purple-600 dark:text-purple-400">return</span> result.status === <span className="text-green-600 dark:text-green-400">"ok"</span>;</span>
                  </div>

                   {/* Line 4 */}
                  <div className="flex gap-4 opacity-50">
                      <span className="w-6 text-right select-none text-muted-foreground">15</span>
                      <span>{'}'}</span>
                  </div>

                   {/* Comment 3: Return Value */}
                   <div className="ml-10 my-3 border-l-2 border-neutral-950 dark:border-neutral-100 pl-4 py-2 bg-neutral-50 dark:bg-neutral-900/50">
                      <div className="flex items-center gap-2 mb-1">
                           <div className="w-2 h-2 bg-blue-500 rounded-none"></div>
                           <span className="font-bold text-blue-600 dark:text-blue-400 text-[10px] uppercase tracking-wider">Suggestion</span>
                           <span className="text-muted-foreground text-[10px]">Return Value</span>
                      </div>
                      <p className="text-muted-foreground">The function returns only a boolean. Returning a structured result (e.g., provider ID, error code) can improve debugging and API clarity.</p>
                  </div>
              </div>
            </div>
          </div>
        </Section>

        {/* Integrations */}
        <Section>
          <div className="p-4 border-b border-neutral-950 dark:border-neutral-100 bg-neutral-50 dark:bg-neutral-900/20 font-mono text-xs font-bold uppercase tracking-widest text-center">
            § 3.0 Supported Models
          </div>
          <div className="py-16 grayscale opacity-80 hover:grayscale-0 hover:opacity-100 transition-all duration-300">
            <ProviderLogos />
          </div>
        </Section>

        {/* Features */}
        <Section className="p-12 relative" label="§ 4.0 FEATURES">
           <FeaturesSection />
        </Section>

        {/* Pricing */}
        <Section className="p-12 relative" label="§ 5.0 PRICING">
          <div className="max-w-4xl mx-auto pt-8">
            <div className="mb-16 text-center">
              <h2 className="text-3xl font-bold tracking-tight mb-4 font-mono">TRANSPARENT PRICING</h2>
              <p className="text-lg text-muted-foreground font-mono">Predictable costs for your engineering budget.</p>
            </div>

            <div className="grid md:grid-cols-2 gap-0 border border-neutral-950 dark:border-neutral-100">
              <div className="border-r border-neutral-950 dark:border-neutral-100 p-0">
                <PricingTier
                  name="Free Tier"
                  price="$0"
                  description="For hobbyists and open source."
                  features={[
                    "100 scans / month",
                    "GPT-4o Mini",
                    "Community Support",
                    "Public Repositories"
                  ]}
                  buttonText="Start Free"
                  onButtonClick={handleGetStarted}
                />
              </div>
              <div className="bg-neutral-50 dark:bg-neutral-900/10 p-0">
                <PricingTier
                  name="Pro Team"
                  price="$18"
                  description="For professional development teams."
                  features={[
                    "Unlimited scans",
                    "Claude 3.5 Sonnet & GPT-4o",
                    "Priority Email Support",
                    "Private Repositories",
                    "Custom Rules Engine"
                  ]}
                  buttonText="Upgrade to Pro"
                  highlighted
                  onButtonClick={handleGetStarted}
                />
              </div>
            </div>
          </div>
        </Section>

        {/* FAQ */}
        <Section className="p-12 max-w-4xl mx-auto relative" label="§ 6.0 FAQ" noBorder>
           {/* noBorder because Footer is the bottom cap, but usually SheetLayout handles outer border.
               Wait, SheetLayout puts a border around everything.
               But sections usually divide internally.
               The last section might not need a border if the footer adds one on top?
               The footer has `border-t-2` in the previous code?
               Wait, LandingFooter code I wrote: <footer className="py-12... bg-neutral-50">. It does NOT have a border-t.
               So the last Section should probably have a border-b to separate from Footer?
               Or the Footer should have border-t.
               Let's check LandingFooter.tsx content again.
               I wrote: <footer className="py-12 bg-neutral-50 dark:bg-neutral-900">
               It doesn't have a top border.
               So I should add `border-t-2` to LandingFooter or keep `border-b-2` on the FAQ section.
               Actually, the FAQ section is inside <main>. Footer is after <main>.
               The FAQ section having a border-b separates it from the Footer.
               Let's look at the "Sheet" concept. The Footer is inside the Sheet border.
               So separation is good.
               However, I'll check my `LandingFooter` code.
               I wrote `LandingFooter` without a border.
               I'll add `border-t-2` to `LandingFooter` to be safe and explicit, or rely on the previous section's border.
               If I rely on previous section, then FAQ needs border.
               I'll remove `noBorder` from FAQ and let it have a bottom border.
           */}
          <h2 className="text-2xl font-bold mb-12 font-mono pt-4">FREQUENTLY ASKED QUESTIONS</h2>
          <div className="border border-neutral-950 dark:border-neutral-100 p-8 rounded-none">
            <FAQSection />
          </div>
        </Section>

      </main>

      <LandingFooter />
    </SheetLayout>
  );
};

export default LandingPage;
